import { z } from 'zod';

/** PRE-8 roadmap read/mutation contracts. Mirrors the DB enums in @prepzio/db. */
export const roadmapItemKindSchema = z.enum(['main_goal', 'practice', 'reflection']);
export type RoadmapItemKindDto = z.infer<typeof roadmapItemKindSchema>;

export const roadmapItemStatusSchema = z.enum(['pending', 'in_progress', 'completed']);
export type RoadmapItemStatusDto = z.infer<typeof roadmapItemStatusSchema>;

export const roadmapItemViewSchema = z.object({
  id: z.string(),
  kind: roadmapItemKindSchema,
  title: z.string(),
  description: z.string(),
  skill_id: z.string().nullable(),
  session_type: z.string().nullable(),
  estimated_minutes: z.number().nullable(),
  status: roadmapItemStatusSchema,
  order_index: z.number(),
});
export type RoadmapItemView = z.infer<typeof roadmapItemViewSchema>;

/** One day of the plan: its ordered items (a main goal + practice + reflection). */
export const roadmapDaySchema = z.object({
  day_index: z.number(),
  items: z.array(roadmapItemViewSchema),
});
export type RoadmapDay = z.infer<typeof roadmapDaySchema>;

/**
 * PRE-134 (BR-71) — "graduation-lite" completion summary. Present (non-null) only
 * when the roadmap is `completed`, so the client can render a meaningful finish
 * moment (counts + a prominent reassess CTA) instead of a bare completed plan.
 * `reassess` is always true today: completion never auto-generates a new plan
 * (BR-67) — the next roadmap is born from a fresh scored session (BR-56).
 */
export const roadmapCompletionSchema = z.object({
  total_items: z.number(),
  completed_items: z.number(),
  day_count: z.number(),
  reassess: z.boolean(),
  /** PRE-149: calendar-day streak at completion, for the finish-moment reward. */
  streak: z.object({ current: z.number(), longest: z.number() }),
});
export type RoadmapCompletion = z.infer<typeof roadmapCompletionSchema>;

export const roadmapResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  source_session_id: z.string().nullable(),
  /** BR-89: which signal chose this plan's skills — measurement, or a target
   * role's CV↔JD gap. Null on plans generated before it was recorded. */
  source_signal: z.enum(['measurement', 'cv_jd_gap']).nullable(),
  created_at: z.string(),
  days: z.array(roadmapDaySchema),
  /** Non-null only for a `completed` roadmap (BR-71); null while active. */
  completion: roadmapCompletionSchema.nullable(),
});
export type RoadmapResponse = z.infer<typeof roadmapResponseSchema>;

/**
 * PRE-134 (BR-72) — graduation / exit-assessment result. A graduation session
 * re-measures the completed roadmap's target skills; each skill passes when its
 * mastery clears `mastery_threshold`. `outcome` is `graduated` only when every
 * target skill passed; otherwise `reloop`, and `reloop_roadmap_id` points at the
 * fresh roadmap generated from the still-weak skills (BR-56 supersede).
 */
export const graduationSkillResultSchema = z.object({
  skill_id: z.string(),
  slug: z.string(),
  title: z.string(),
  /** Mean concept-mastery for this skill in the graduation session, or null if uncovered. */
  score: z.number().nullable(),
  passed: z.boolean(),
});
export type GraduationSkillResult = z.infer<typeof graduationSkillResultSchema>;

/**
 * PRE-150 — next-step options offered when a learner `graduated`. They can either
 * level up (same target skills at higher difficulty) or move to a new track
 * (skills not yet drilled). Each option carries an `available` flag; the client
 * shows only available ones and posts the chosen one to /graduation/next.
 */
export const graduationNextOptionsSchema = z.object({
  level_up: z.object({ available: z.boolean() }),
  new_track: z.object({
    available: z.boolean(),
    skill_slugs: z.array(z.string()),
  }),
});
export type GraduationNextOptions = z.infer<typeof graduationNextOptionsSchema>;

export const graduationNextChoiceSchema = z.object({
  choice: z.enum(['level_up', 'new_track']),
});
export type GraduationNextChoice = z.infer<typeof graduationNextChoiceSchema>;

export const graduationResultSchema = z.object({
  roadmap_id: z.string(),
  session_id: z.string(),
  outcome: z.enum(['graduated', 'reloop']),
  mastery_threshold: z.number(),
  skills: z.array(graduationSkillResultSchema),
  /** New roadmap id when outcome is `reloop`; null on `graduated`. */
  reloop_roadmap_id: z.string().nullable(),
  /** PRE-150: next-step options — present only when outcome is `graduated`, else null. */
  next_options: graduationNextOptionsSchema.nullable(),
});
export type GraduationResult = z.infer<typeof graduationResultSchema>;

/**
 * PRE-128 — structured output contract for the LLM roadmap generator.
 *
 * The model authors the *content* (personalized titles + descriptions) for a
 * 7-day plan; the server owns the structure (kinds, order, minutes) and maps an
 * optional `skill_slug` back to a real weak-skill id. `skill_slug` must be one
 * of the weak-skill slugs given in the prompt, else the server drops it to null.
 * A goal/practice item is a `roadmap_practice` session; reflections are prompts.
 */
export const roadmapPlanItemSchema = z.object({
  title: z.string().min(1).max(120),
  description: z.string().min(1).max(500),
  /** Optional weak-skill slug this item targets; null/omitted for general work. */
  skill_slug: z.string().min(1).max(120).nullish(),
});
export type RoadmapPlanItem = z.infer<typeof roadmapPlanItemSchema>;

export const roadmapPlanDaySchema = z.object({
  day_index: z.number().int().min(1).max(7),
  main_goal: roadmapPlanItemSchema,
  practice: roadmapPlanItemSchema,
  reflection: roadmapPlanItemSchema,
});
export type RoadmapPlanDay = z.infer<typeof roadmapPlanDaySchema>;

export const roadmapPlanSchema = z.object({
  days: z.array(roadmapPlanDaySchema).length(7),
});
export type RoadmapPlan = z.infer<typeof roadmapPlanSchema>;

/** Strategy recorded on `roadmap_generated` — which engine authored the plan. */
export const ROADMAP_STRATEGIES = ['rule_based_v0', 'llm_v1'] as const;
export const roadmapStrategySchema = z.enum(ROADMAP_STRATEGIES);
export type RoadmapStrategy = z.infer<typeof roadmapStrategySchema>;
